/**
 * Gems drawn as cut stones, for the client side (Ryan, 2026-09-27): the style profile's gem "shouldn't be the same
 * play button as our logo", and the Gem Bag tier in the sidebar wants "real gem graphics". The gem mark stays the
 * brand's; these are the stones.
 *
 * One cut for both: a round brilliant seen from the side (a flat table, the crown sloping out to the girdle, the
 * pavilion down to a point), because that silhouette still reads as a gem at 10px, where a stone seen face-up reads
 * as a button. It is drawn on a 32-unit grid scaled to the box, with every horizontal line (table, girdle) rounded to
 * a whole pixel so the edges stay sharp at sidebar sizes. Under 20px it shows fewer, larger facets: the full set
 * turns to mush at 14px.
 *
 * Facets are flat fills, no gradients: sharper when small, and no ids to collide when a page shows several.
 *
 * FacetedGem  the style profile's gem: tone "color" (the website's teals) or "grey" (the empty part). The two share
 *             one silhouette, so GemProgress stacks them and clips the colour one with gemFillClip.
 * TierGem     a Gem Bag tier's stone, every tone mixed from the one colour the server sends (badge_color), so any
 *             tier, present or future, works without a table here. `onLight` for a pale ground: near-white stones
 *             (Quartz, Diamond) take darker tones and an edge there, or they vanish into the paper. The sidebar is
 *             black in both themes and never needs it.
 */

// ── The cut ──
// Points on the 32-unit grid, as distances from the centre line. Table: back edge 5.5 either side, front 8.5 (the
// table seen from a little above, a thin lit plane). Girdle 15 either side.
function cut(size) {
  const u = size / 32;
  const cx = size / 2;
  const y = v => Math.round(v * u);
  const at = (dx, py) => [cx + dx * u, py];
  const back = y(2.5), front = y(4.5), gir = y(11.5), bot = size - y(3);
  const table = front > back; // at 10px the plane rounds to nothing: the table is then just the top edge
  const top = table ? back : front;
  const C = at(0, bot);
  const G0 = at(-15, gir), G3 = at(15, gir);
  const F0 = at(-8.5, front), F2 = at(8.5, front);
  const B0 = at(-5.5, back), B1 = at(5.5, back);
  const outline = table ? [B0, B1, F2, G3, C, G0, F0] : [F0, F2, G3, C, G0];
  const plane = table ? [[[B0, B1, F2, F0], 'hi']] : [];
  if (size < 20) {
    // three crown facets and three pavilion facets: the centre ones lit, the right ones in shade
    const Fa = at(-2.2, front), Fb = at(2.2, front), Ga = at(-6.4, gir), Gb = at(6.4, gir);
    return { top, bot, outline, facets: [
      ...plane,
      [[G0, F0, Fa, Ga], 'light'], [[Fa, Fb, Gb, Ga], table ? 'bright' : 'hi'], [[Fb, F2, G3, Gb], 'base'],
      [[G0, Ga, C], 'base'], [[Ga, Gb, C], 'light'], [[Gb, G3, C], 'dark'],
    ] };
  }
  // the crown as a row of triangles (star and bezel facets), the pavilion as six slivers meeting at the point;
  // light from the top left
  const F1 = at(0, front), G1 = at(-5, gir), G2 = at(5, gir), M = at(0, gir), Q0 = at(-10, gir), Q1 = at(10, gir);
  return { top, bot, outline, facets: [
    ...plane,
    [[G0, F0, G1], 'light'], [[F0, F1, G1], 'bright'], [[G1, F1, G2], 'base'], [[F1, F2, G2], 'bright'], [[G2, F2, G3], 'dark'],
    [[G0, Q0, C], 'bright'], [[Q0, G1, C], 'base'], [[G1, M, C], 'light'], [[M, G2, C], 'base'], [[G2, Q1, C], 'dark'], [[Q1, G3, C], 'deep'],
  ] };
}

const points = list => list.map(([x, y]) => `${+x.toFixed(3)},${+y.toFixed(3)}`).join(' ');

// The outline moved inward by d (it is convex: each edge slides along its inward normal, each corner is where two
// slid edges meet). With the outline it makes an evenodd ring: an edge that never paints outside the stone.
function inset(poly, d) {
  const n = poly.length;
  let area = 0;
  for (let i = 0; i < n; i++) area += poly[i][0] * poly[(i + 1) % n][1] - poly[(i + 1) % n][0] * poly[i][1];
  const side = area > 0 ? 1 : -1;
  const edges = poly.map((p, i) => {
    const q = poly[(i + 1) % n];
    const dx = q[0] - p[0], dy = q[1] - p[1], len = Math.hypot(dx, dy);
    return { x: p[0] - (dy / len) * side * d, y: p[1] + (dx / len) * side * d, dx, dy };
  });
  return edges.map((b, i) => {
    const a = edges[(i + n - 1) % n];
    const t = ((b.x - a.x) * b.dy - (b.y - a.y) * b.dx) / (a.dx * b.dy - a.dy * b.dx);
    return [a.x + a.dx * t, a.y + a.dy * t];
  });
}

function Stone({ size, tones, edge = 0, opacity, className, style }) {
  const g = cut(size);
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-hidden="true" focusable="false"
      className={className} style={{ display: 'block', flex: 'none', ...style }}>
      <g opacity={opacity}>
        {/* the whole stone first, so the seams between facets show stone and not the ground */}
        <polygon points={points(g.outline)} fill={tones.base} />
        {g.facets.map(([facet, tone], i) => <polygon key={i} points={points(facet)} fill={tones[tone]} />)}
        {edge > 0 && (
          <path d={`M${points(g.outline)}Z M${points(inset(g.outline, Math.max(1, size * 0.035)))}Z`}
            fillRule="evenodd" fill={tones.dark} fillOpacity={0.85 * edge} />
        )}
      </g>
    </svg>
  );
}

// ── The style profile's gem ──
const TEAL = { hi: '#d8faf5', light: '#8ae8dc', bright: '#3fd6c4', base: '#2bb3a8', dark: '#1b7f77', deep: '#0f524d' };
// the empty part: neutral greys at 30%, which settle to a dim stone on the black card and a pale one on white
const GREY = { hi: '#fafafa', light: '#e4e4e7', bright: '#d4d4d8', base: '#a1a1aa', dark: '#71717a', deep: '#52525b' };

export function FacetedGem({ size = 32, tone = 'color', className, style }) {
  const grey = tone === 'grey';
  return <Stone size={size} tones={grey ? GREY : TEAL} opacity={grey ? 0.3 : undefined} className={className} style={style} />;
}

// The clip-path for the colour layer of a filling gem, lit from the bottom up: pct is mapped onto the stone's own
// height (point to table), not the box's, so 5% already shows at the point and 100% reaches the table exactly.
export function gemFillClip(pct, size = 32) {
  const { top, bot } = cut(size);
  const fill = Math.max(0, Math.min(100, Number(pct) || 0)) / 100;
  return `inset(${+(top + (1 - fill) * (bot - top)).toFixed(2)}px 0 0 0)`;
}

// ── A tier's stone ──
function hexToRgb(hex) {
  const h = String(hex || '').trim().replace(/^#/, '');
  const full = h.length === 3 ? h.split('').map(c => c + c).join('') : h;
  if (!/^[0-9a-f]{6}$/i.test(full)) return null;
  const n = parseInt(full, 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}
// t > 0 mixes toward white, t < 0 toward black
const shade = (rgb, t) => '#' + rgb.map(c => Math.round(t >= 0 ? c + (255 - c) * t : c * (1 + t)).toString(16).padStart(2, '0')).join('');
const luminance = rgb => {
  const [r, g, b] = rgb.map(c => { const v = c / 255; return v <= 0.04045 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4; });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
};
// Each tone as a mix of the tier colour: [a coloured stone, a near-white one]. A near-white stone's lit tones are all
// but equal, so its ramp moves down: half way on a dark ground (the facets part, the stone stays bright), all the way
// on paper, where its shaded facets must carry the shape the paper takes from its lit ones.
const RAMP = { hi: [0.75, 0.7], light: [0.42, 0], bright: [0.2, -0.1], base: [0, -0.2], dark: [-0.3, -0.42], deep: [-0.5, -0.6] };

export function TierGem({ color, size = 14, onLight = false, className, style }) {
  const rgb = hexToRgb(color) || [148, 163, 184]; // Standard's slate when the colour is missing or not hex
  // how pale the stone is: 0 for the coloured tiers (Amber, the brightest, about 0.1), about 1 for Quartz and Diamond
  const pale = Math.min(1, Math.max(0, (luminance(rgb) - 0.55) / 0.3));
  const shift = onLight ? pale : pale / 2;
  const tones = {};
  for (const [name, [coloured, white]] of Object.entries(RAMP)) tones[name] = shade(rgb, coloured + (white - coloured) * shift);
  return <Stone size={size} tones={tones} edge={onLight ? pale : 0} className={className} style={style} />;
}
